import queryString from "query-string";
import { useEffect, useState } from "react";

import { useDispatch } from "react-redux";
import { useLocation } from "react-router";
import { Redirect } from "react-router-dom";

import { toast } from "@/design-system/components/Toast/Toast";

import { fetchAccountMetadata } from "@/actions/environments";
import { fetchUsers } from "@/actions/users";

import { makeRequest } from "@/util";
import { ConfirmSwitchTeams } from "./ConfirmSwitchTeams";
import { InvalidInvitePage } from "./InvalidInvitePage";
import { AcceptInviteResponse, AcceptInvitePageProps } from "./types";

// The logic here is a bit tricky. There are two different entry points
// into accepting an invite and there's some overlap between them.
//
// If the user clicks the accept invite link in the email, they get dropped
// into the AcceptInvitePage. If there's no problem accepting the invite, we
// accept it. If there was a problem, some scenarios we have to handle:
// - The invite was expired or sent to someone else -> we show an error message
// - The user is already part of an existing account -> we show a confirmation
//   page to confirm the user wants to switch accounts
//
// If the user logs in and they have pending invite to another account, they
// will land on PendingInvitePage. This will ask them to confirm if they want
// to switch accounts, and if so, we re-use the same logic as the
// AcceptInvitePage _after_ the user has confirmed they want to switch accounts.
//

function AcceptInvitePage(props) {
  if (!props.isSignedIn) {
    // prevent the next URL from sending user to sign out page
    const next = props.location.pathname.includes("signout")
      ? null
      : props.location.pathname + props.location.search;
    return <Redirect to={`/login?${queryString.stringify({ next })}`} />;
  } else {
    return <AcceptInvitePageHelper {...props} />;
  }
}

function AcceptInvitePageHelper({
  transferConfirmed = false,
  pendingInviteID,
}: AcceptInvitePageProps) {
  const location = useLocation();
  const dispatch = useDispatch();

  const [transferTeamsConfirmed, setTransferTeamsConfirmed] =
    useState(transferConfirmed);
  const [needConfirmation, setNeedConfirmation] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [invalidInviteReason, setInvalidInviteReason] = useState<number | null>(
    null,
  );
  const [invalidInviteDetails, setInvalidInviteDetails] = useState("");

  const inviteID = pendingInviteID || queryString.parse(location.search).id;

  useEffect(() => {
    makeRequest("accept_invite", {
      invite_id: inviteID,
      confirmed: transferTeamsConfirmed,
    })
      .then((response) => {
        const data = response.data as AcceptInviteResponse;
        if (data.result === "success") {
          setInviteSuccess(true);
          dispatch(fetchAccountMetadata());
          dispatch(fetchUsers());
        } else if (data.result === "invalid") {
          setInvalidInviteReason(data.invalid_reason || 0);
          setInvalidInviteDetails(data.details || "");
        } else if (data.result === "needs_confirmation") {
          setNeedConfirmation(true);
        }
      })
      .catch((error) => {
        toast.error("Something went wrong. Unable to load page.");
        console.error(error);
      });
  }, [transferTeamsConfirmed, inviteID, dispatch]);

  if (inviteSuccess) {
    return <Redirect to="/events" />;
  } else if (needConfirmation && inviteID) {
    return (
      <ConfirmSwitchTeams
        inviteID={inviteID as string}
        onConfirm={() => setTransferTeamsConfirmed(true)}
      />
    );
  } else if (invalidInviteReason) {
    return (
      <InvalidInvitePage
        reason={invalidInviteReason}
        details={invalidInviteDetails}
      />
    );
  } else {
    return null;
  }
}

export default AcceptInvitePage;
